/**
 * Which name a Resynchronization row shows for a synced item.
 *
 * Monday is the source of truth for names. The database's `itemname` is written once,
 * at sync setup, and no rename handler ever updates it, so nothing here may present it
 * as the item's name. Every input below is a LIVE Monday name; the question this module
 * answers is only which live name, because a sync group has one mirror per board and
 * after a rename they can disagree.
 *
 * Inputs, and where each live name comes from:
 * - `rowData` (a verdict, when one has arrived) carries the names per board in one of
 *   two shapes. A cached verdict (`disc:flags:<syncId>`) has `board_names[]`, each
 *   `{ board_id, item_name }`. A streamed job result or a row-expand result has
 *   `items[]`, each `{ board: { id }, name }`. Either may be present; both are per board.
 * - `rowData.item_name` is the engine's own pick: the FIRST mirror in the group, which
 *   is not necessarily the board being viewed.
 * - `syncItem.item_name` is the board view's page list, which the backend now fills
 *   with the live name of the mirror on the viewing board (absent in Discrepancies
 *   mode, where no page list is fetched).
 */

export interface DisplayNameSources {
  rowData?: {
    item_name?: string;
    board_names?: Array<{ board_id?: string | number; item_name?: string }>;
    items?: Array<{ board?: { id?: string | number }; name?: string }>;
  } | null;
  syncItem?: { item_name?: string } | null;
  /** The board the user is viewing this page from. */
  currentBoard?: string | number | null;
}

const sameId = (a: string | number | null | undefined, b: string | number | null | undefined) =>
  a !== undefined && a !== null && b !== undefined && b !== null && String(a) === String(b);

/**
 * The live name of the mirror on `currentBoard`, from whichever verdict shape arrived.
 * An empty string counts as absent: a board whose mirror has no name is not the board
 * to label the row by.
 */
const mirrorNameOnBoard = (
  rowData: DisplayNameSources["rowData"],
  currentBoard: DisplayNameSources["currentBoard"],
): string | undefined => {
  if (!rowData || currentBoard === undefined || currentBoard === null) return undefined;

  const cached = rowData.board_names?.find((entry) => sameId(entry.board_id, currentBoard));
  if (cached?.item_name) return cached.item_name;

  const streamed = rowData.items?.find((item) => sameId(item.board?.id, currentBoard));
  if (streamed?.name) return streamed.name;

  return undefined;
};

/**
 * The name to render, or "" when no source has one.
 *
 * Order: the viewing board's own mirror (the name the user can read on the board
 * beside this view), then the engine's pick (some mirror's live name, when this board
 * has none in the verdict — deleted here, or the verdict predates the link), then the
 * page list's live name (the only source before a verdict arrives). Never the database.
 */
export const resolveItemDisplayName = ({
  rowData,
  syncItem,
  currentBoard,
}: DisplayNameSources): string =>
  mirrorNameOnBoard(rowData, currentBoard) || rowData?.item_name || syncItem?.item_name || "";
